import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { useFormik } from 'formik';


// Constants
import ROUTE from '../../constants/route-constants';
import { OWNER_ROLE, ROLES } from '../../constants/roles';
import { EMAIL_LENGTH_LIMITS, TEXT_LENGTH_LIMITS } from '../../constants/common';

// Store
import { Dispatch, RootState } from '../../store/store';

// Components
import InteractiveButton from '../../components/InteractiveButton/InteractiveButton';
import InputControl from '../../components/InputControl/InputControl';
import SwitcherOne from '../../components/Switchers/SwitcherOne';
import SelectGroupOne from '../../components/Forms/SelectGroup/SelectGroupOne';
import MultiSelectOption from '../../components/MultiSelectOption/MultiSelectOption';
import SpinnerLoader from '../../components/SpinnerLoader/SpinnerLoader';
import generator from 'generate-password-ts';
import { PASSWORD_RULES_HINT, getPasswordStrengthError } from '../../utils/passwordRules';
import { toast } from 'react-toastify';
import IconCopy from '../../components/Icons/IconCopy/IconCopy';
import { ElementSize } from '../../types/element-size';

type CreateMemberProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

const CreateMember: React.FC<CreateMemberProps> = ({ createMember, getLocations, locations }) => {
  const [isCopiedPwd, setIsCopiedPwd] = useState<boolean>(false);

  const navigate = useNavigate();

  let options = Object.values(ROLES).map((role) => ({
    value: role,
    label: role,
    disabled: false,
  }));

  options = [
    { value: '', label: 'Choose member role', disabled: true },
    ...options,
  ];

  const [loading, setLoading] = useState<boolean>(true);

  const formik = useFormik({
    initialValues: {
      email: '',
      temporary_password: '',
      last_name: '',
      first_name: '',
      role: '',
      active: false,
      locations: [],
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email('Invalid email format')
        .min(EMAIL_LENGTH_LIMITS.MIN, `Minimum ${EMAIL_LENGTH_LIMITS.MIN} characters required`)
        .max(EMAIL_LENGTH_LIMITS.MAX, `Maximum ${EMAIL_LENGTH_LIMITS.MAX} characters required`)
        .required('Required'),
      temporary_password: Yup.string()
        .notRequired()
        .test('password-strength', function (value) {
          if (!value) return true;

          const error = getPasswordStrengthError(value);

          return error ? this.createError({ message: error }) : true;
        }),
      first_name: Yup.string()
        .required('Required')
        .min(TEXT_LENGTH_LIMITS.MIN, `Min first name length is ${TEXT_LENGTH_LIMITS.MIN} characters`)
        .max(TEXT_LENGTH_LIMITS.MAX, `Max first name length is ${TEXT_LENGTH_LIMITS.MAX} characters`),
      last_name: Yup.string()
        .required('Required')
        .min(TEXT_LENGTH_LIMITS.MIN, `Min last name length is ${TEXT_LENGTH_LIMITS.MIN} characters`)
        .max(TEXT_LENGTH_LIMITS.MAX, `Max last name length is ${TEXT_LENGTH_LIMITS.MAX} characters`),
      role: Yup.string().required(),
      active: Yup.boolean(),
    }),
    onSubmit: async (values) => {
      const { locations, temporary_password, ...restData } = values;
      if (temporary_password && !isCopiedPwd) {
        toast.error('You didn\'t copy the password!');
        return;
      }

      const locationIds = locations.map((location) => location.id);
      const password = temporary_password ? temporary_password : undefined;

      await createMember({ ...restData, locationIds, temporary_password: password });
      navigate(ROUTE.MEMBERS);
    },
  });

  const isDisabledBtn = Boolean((formik.touched.email && formik.errors.email) || (formik.touched.first_name && formik.errors.first_name) || (formik.touched.last_name && formik.errors.last_name) || (formik.touched.role && formik.errors.role) || (formik.touched.temporary_password && formik.errors.temporary_password));

  const handleSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = event.target;

    formik.handleChange({
      target: { name, value },
    });

    if (name === 'role' && value === OWNER_ROLE) {
      formik.handleChange({
        target: { name: 'locations', value: getOptions() },
      });
    }
  };

  const fetchData = async () => {
    await getLocations();
    setLoading(false);
  }

  const getOptions = () => {
    return locations.map((location) => ({ id: location.id, value: location.name }));
  }

  const selectLocations = (data: { name: string, selectedOptions: any[] }) => {
    formik.handleChange({
      target: { name: data.name, value: data.selectedOptions },
    });
  }

  const generatePassword = () => {
    const password = generator.generate({
      length: 12,
      numbers: true,
      symbols: true,
      uppercase: true,
      lowercase: true,
      strict: true,
    });

    formik.setFieldValue('temporary_password', password)
    setIsCopiedPwd(false)
  }

  const handleCopy = async () => {
    if (formik.values.temporary_password) {
      await navigator.clipboard.writeText(formik.values.temporary_password);
      setIsCopiedPwd(true)
      toast.success('Password has been copied');
    } else {
      toast.error('Password field is empty');
    }
  }

  const handlePwdChange = (value) => {
    formik.setFieldValue('temporary_password', value)
    setIsCopiedPwd(false)
  }

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="p-create-member pb-4 md:pb-25">
      {loading ? (<div className="h-full w-full flex items-center justify-center">
        <SpinnerLoader />
      </div>) : (
        <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
          <div className="border-b border-stroke px-7 py-4 dark:border-strokedark">
            <h3 className="font-medium text-black dark:text-white">
              Create Member
            </h3>
          </div>
          <form className="flex flex-col gap-5.5 p-7" onSubmit={formik.handleSubmit}>
            <div className="w-full">
              <InputControl
                label="Email"
                type="email"
                name="email"
                placeholder="Enter new member email"
                value={formik.values.email}
                customOnChange={formik.handleChange}
                onBlur={formik.handleBlur}
                showError={formik.touched.email && formik.errors.email}
                errorText={formik.errors.email}
              />
            </div>
            <div className="w-full flex flex-col xsm:flex-row xsm:items-center gap-4">
              <div className='w-full max-w-96'>
                <InputControl
                  label="Temporary Password (optional)"
                  name="temporary_password"
                  placeholder="Enter Temporary Password"
                  value={formik.values.temporary_password}
                  customOnChange={(e) => handlePwdChange(e.target.value)}
                  onBlur={formik.handleBlur}
                  showError={formik.touched.temporary_password && Boolean(formik.errors.temporary_password)}
                  errorText={formik.errors.temporary_password}
                  suggestion={PASSWORD_RULES_HINT}
                  iconRight={<IconCopy size={ElementSize.MD} onClick={handleCopy} fill="#d5d7db" />}
                />
              </div>
              <InteractiveButton className='min-h-13' text="Generate" onClick={generatePassword} />
            </div>
            <div className="w-full">
              <InputControl
                label="First Name"
                type="text"
                name="first_name"
                placeholder="Enter new member first name"
                value={formik.values.first_name}
                customOnChange={formik.handleChange}
                onBlur={formik.handleBlur}
                showError={formik.touched.first_name && formik.errors.first_name}
                errorText={formik.errors.first_name}
              />
            </div>
            <div className="w-full">
              <InputControl
                label="Last Name"
                type="text"
                name="last_name"
                placeholder="Enter new member last name"
                value={formik.values.last_name}
                customOnChange={formik.handleChange}
                onBlur={formik.handleBlur}
                showError={formik.touched.last_name && formik.errors.last_name}
                errorText={formik.errors.last_name}
              />
            </div>
            <div className="w-full">
              <SelectGroupOne
                label="Role"
                name='role'
                handleChange={handleSelect}
                options={options}
                selectedOption={formik.values.role}
                showError={formik.touched.role && formik.errors.role}
                errorText={formik.errors.role}
              />
            </div>
            <MultiSelectOption updateValues={selectLocations} name="locations" title='Multiselect clients' listOptions={getOptions()} disabled={formik.values.role === OWNER_ROLE} />
            <SwitcherOne
              name="active"
              enabled={formik.values.active}
              handleChange={formik.handleChange}
              label="Active"
            />
            <div className="flex justify-end gap-4.5">
              <InteractiveButton
                text="Create"
                disabled={isDisabledBtn}
                submit
                isLoading={formik.isSubmitting}
              />
            </div>
          </form>
        </div>
      )}
    </div >
  );
};

const mapState = (state: RootState) => ({
  locations: state.locations.locations,
});

const mapDispatch = (dispatch: Dispatch) => ({
  createMember: dispatch.members.createMember,
  getLocations: dispatch.locations.getLocations,
});

export default connect(mapState, mapDispatch)(CreateMember);
